import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag, AlertOctagon } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog de signalement — US-17 CDC v1.0
 * 6 catégories obligatoires, SLA 4h pour revue admin.
 */
export default function ReportPostDialog({ open, onOpenChange, postId }) {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/moderation/report-categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Sélectionnez une catégorie.");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${postId}/report`, { category: selected, comment });
      if (data.duplicate) toast.info("Vous avez déjà signalé cette publication.");
      else toast.success("Signalement envoyé · revue sous 4 h par l'équipe.");
      setSelected(""); setComment("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-red-600" />Signaler cette publication
        </DialogTitle>
        <DialogDescription>
          Aidez-nous à maintenir la qualité de Doctobook. Tout signalement est examiné sous 4 h par notre équipe modération.
        </DialogDescription>

        <form onSubmit={submit} className="space-y-3 mt-3">
          <div className="space-y-1.5">
            {categories.map((c) => (
              <label key={c.key} className={`flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer border transition ${selected === c.key ? "border-red-300 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  type="radio"
                  name="category"
                  value={c.key}
                  checked={selected === c.key}
                  onChange={() => setSelected(c.key)}
                  className="mt-0.5 accent-red-600"
                  data-testid={`report-cat-${c.key}`}
                />
                <span className="text-sm text-slate-700 leading-snug">{c.label}</span>
              </label>
            ))}
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Précisions (facultatif)…"
            data-testid="report-comment"
          />
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Annuler</Button>
            <Button type="submit" disabled={!selected || submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="report-submit-btn">
              <AlertOctagon className="h-4 w-4 me-1" />{submitting ? "Envoi…" : "Signaler"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
